import { useState, useEffect, useMemo } from 'react';
import {
  GitCompareArrows,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  Filter,
  Building2,
  Tag,
  X,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { api } from '@/lib/api';
import type { AnalyticsFiltersData, CompareResult, ModoAlicante } from '@/lib/types';
import { ZONE_LABELS, classNames } from '@/lib/utils';
import { monthRange, yearRange, shiftMonths, shiftYears, rangeLabel, DateRange } from '@/lib/analyticsDates';
import { KPI_INFO } from '@/lib/kpiInfo';
import MultiSelect from '@/components/ui/MultiSelect';
import InfoTooltip from '@/components/ui/InfoTooltip';
import PanelFiltros from '@/components/analytics/PanelFiltros';
import FiltroAlicante from '@/components/analytics/FiltroAlicante';
import { useIsDarkMode } from '@/hooks/useIsDarkMode';

type ComparisonMode = string;

interface ComparePresetDef {
  id: ComparisonMode;
  label: string;
  build: () => { current: DateRange; previous: DateRange };
}

// Meses del año en curso comparados con el mismo mes del año anterior
const MONTH_VS_LAST_YEAR: { id: string; label: string; month: number }[] = [
  { id: 'jun_vs_ly', label: 'Junio vs año anterior', month: 5 },
  { id: 'jul_vs_ly', label: 'Julio vs año anterior', month: 6 },
  { id: 'ago_vs_ly', label: 'Agosto vs año anterior', month: 7 },
  { id: 'sep_vs_ly', label: 'Septiembre vs año anterior', month: 8 },
];

const COMPARE_PRESETS: ComparePresetDef[] = [
  {
    id: 'prev_month',
    label: 'Este mes vs mes anterior',
    build: () => {
      const n = new Date();
      const current = monthRange(n.getFullYear(), n.getMonth());
      return { current, previous: shiftMonths(current, -1) };
    },
  },
  {
    id: 'prev_year',
    label: 'Este mes vs mismo mes año anterior',
    build: () => {
      const n = new Date();
      const current = monthRange(n.getFullYear(), n.getMonth());
      return { current, previous: shiftYears(current, -1) };
    },
  },
  ...MONTH_VS_LAST_YEAR.map(({ id, label, month }) => ({
    id,
    label,
    build: () => {
      const year = new Date().getFullYear();
      const current = monthRange(year, month);
      return { current, previous: shiftYears(current, -1) };
    },
  })),
  {
    // Años naturales completos: 1 ene – 31 dic de cada año. Del año en curso
    // entran también las reservas confirmadas de meses futuros.
    id: 'year_vs_prev_year',
    label: 'Año natural vs año anterior',
    build: () => {
      const year = new Date().getFullYear();
      return { current: yearRange(year), previous: yearRange(year - 1) };
    },
  },
];

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

const KPI_ROWS: { key: string; format: (v: number) => string }[] = [
  { key: 'facturacion', format: fmtMoney },
  { key: 'neto', format: fmtMoney },
  { key: 'reservas', format: (v) => String(Math.round(v)) },
  { key: 'noches', format: (v) => String(Math.round(v)) },
  { key: 'huespedes', format: (v) => String(Math.round(v)) },
  { key: 'adr', format: fmtMoney },
  { key: 'estanciaMedia', format: (v) => `${Math.round(v)} noches` },
  { key: 'ocupacion', format: (v) => `${Math.round(v)}%` },
  { key: 'comision', format: fmtMoney },
];

export default function ComparativasPage() {
  const isDark = useIsDarkMode();
  const chart = {
    grid: isDark ? '#ffffff14' : '#0B1D2E15',
    axis: isDark ? '#9ca3af' : '#0B1D2E99',
    barA: isDark ? '#D1E0F0' : '#0B1D2E',
    barB: isDark ? '#4A7BB0' : '#A3C2E0',
    cursor: isDark ? '#ffffff10' : '#0B1D2E08',
  };
  const [filtersData, setFiltersData] = useState<AnalyticsFiltersData | null>(null);
  const [data, setData] = useState<CompareResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<ComparisonMode>('prev_month');

  const initialA = monthRange(new Date().getFullYear(), new Date().getMonth());
  const initialB = shiftMonths(initialA, -1);
  const [fromA, setFromA] = useState(initialA.from);
  const [toA, setToA] = useState(initialA.to);
  const [fromB, setFromB] = useState(initialB.from);
  const [toB, setToB] = useState(initialB.to);

  const [propertyIds, setPropertyIds] = useState<string[]>([]);
  const [zones, setZones] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [includeOwner, setIncludeOwner] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  // Propiedades familiares (Malia Bernal + CasaML): incluidas por defecto
  const [includeFamily, setIncludeFamily] = useState(true);
  // Alicante fuera por defecto: es otra operativa y mezclarla distorsiona las
  // medias. Como «solo activas», cuenta como filtro al SALIRSE de ese estado.
  const [alicante, setAlicante] = useState<ModoAlicante>('sin');
  const [prorate, setProrate] = useState(true);

  useEffect(() => {
    api.getAnalyticsFilters().then(setFiltersData).catch(() => setFiltersData(null));
  }, []);

  const runCompare = async (ov?: {
    fromA?: string;
    toA?: string;
    fromB?: string;
    toB?: string;
    propertyIds?: string[];
    includeFamily?: boolean;
    alicante?: ModoAlicante;
  }) => {
    const a = { from: ov?.fromA ?? fromA, to: ov?.toA ?? toA };
    const b = { from: ov?.fromB ?? fromB, to: ov?.toB ?? toB };
    setLoading(true);
    try {
      const result = await api.compareAnalytics({
        fromA: a.from,
        toA: a.to,
        labelA: rangeLabel(a),
        fromB: b.from,
        toB: b.to,
        labelB: rangeLabel(b),
        propertyIds: ov?.propertyIds ?? propertyIds,
        zones,
        channels,
        includeOwner,
        activeOnly,
        excludeFamily: !(ov?.includeFamily ?? includeFamily),
        alicante: ov?.alicante ?? alicante,
        prorate,
      });
      setData(result);
    } catch (e) {
      console.error(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runCompare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (preset: ComparePresetDef) => {
    const { current, previous } = preset.build();
    setFromA(current.from);
    setToA(current.to);
    setFromB(previous.from);
    setToB(previous.to);
    setMode(preset.id);
    runCompare({ fromA: current.from, toA: current.to, fromB: previous.from, toB: previous.to });
  };

  const propertyOptions = useMemo(() => {
    const list = filtersData?.properties || [];
    let visible = activeOnly ? list.filter((p) => p.active) : list;
    if (!includeFamily) visible = visible.filter((p) => !p.family);
    if (alicante === 'sin') visible = visible.filter((p) => !p.alicante);
    if (alicante === 'solo') visible = visible.filter((p) => p.alicante);
    return visible.map((p) => ({ value: p.id, label: p.name }));
  }, [filtersData, activeOnly, includeFamily, alicante]);
  const zoneOptions = useMemo(() => (filtersData?.zones || []).map((z) => ({ value: z, label: ZONE_LABELS[z] || z })), [filtersData]);
  const channelOptions = useMemo(() => (filtersData?.channels || []).map((c) => ({ value: c, label: c })), [filtersData]);

  // Channel comparison chart data
  const channelChart = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, { name: string; actual: number; anterior: number }>();
    for (const c of data.current.byChannel) map.set(c.channel, { name: c.channel, actual: Math.round(c.facturacion), anterior: 0 });
    for (const c of data.previous.byChannel) {
      const e = map.get(c.channel) || { name: c.channel, actual: 0, anterior: 0 };
      e.anterior = Math.round(c.facturacion);
      map.set(c.channel, e);
    }
    return [...map.values()].sort((a, b) => b.actual - a.actual);
  }, [data]);

  // «Solo activas» e «incluir familiares» son el estado por defecto: cuentan al
  // APAGARSE. Imputar por entrada también suma: cambia los datos y con el panel
  // plegado nada más lo delataría.
  const activeFilterCount =
    propertyIds.length +
    zones.length +
    channels.length +
    (includeOwner ? 1 : 0) +
    (activeOnly ? 0 : 1) +
    (includeFamily ? 0 : 1) +
    (alicante === 'sin' ? 0 : 1) +
    (prorate ? 0 : 1);

  return (
    <div>
      <PanelFiltros
        activos={activeFilterCount}
        barra={
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={mode}
              onChange={(e) => {
                const p = COMPARE_PRESETS.find((x) => x.id === e.target.value);
                if (p) applyPreset(p);
              }}
              className="input min-h-11 w-auto max-w-full"
              aria-label="Comparativa"
            >
              {mode === 'custom' && (
                <option value="custom" disabled>
                  Personalizado
                </option>
              )}
              {COMPARE_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <span className="text-xs text-sereno-dark/50">
              {rangeLabel({ from: fromA, to: toA })} vs {rangeLabel({ from: fromB, to: toB })}
            </span>
          </div>
        }
      >
        {/* El rango a medida no cabe en la barra: se edita aquí y el select pasa a «Personalizado». */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PeriodPicker
            title="Periodo actual (A)"
            from={fromA}
            to={toA}
            onFrom={(v) => { setFromA(v); setMode('custom'); }}
            onTo={(v) => { setToA(v); setMode('custom'); }}
            accent="bg-sereno-dark"
          />
          <PeriodPicker
            title="Periodo de comparación (B)"
            from={fromB}
            to={toB}
            onFrom={(v) => { setFromB(v); setMode('custom'); }}
            onTo={(v) => { setToB(v); setMode('custom'); }}
            accent="bg-sereno-dark/40"
          />
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-4">
          <MultiSelect label="Zonas" icon={Filter} options={zoneOptions} selected={zones} onChange={setZones} allLabel="Todas" />
          <MultiSelect label="Canales" icon={Tag} options={channelOptions} selected={channels} onChange={setChannels} allLabel="Todos" searchable />
          <MultiSelect label="Propiedades" icon={Building2} options={propertyOptions} selected={propertyIds} onChange={setPropertyIds} allLabel="Todas" searchable />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1">
          <label className="min-h-11 flex items-center gap-2 text-sm text-sereno-dark/70 cursor-pointer">
            <input type="checkbox" checked={includeOwner} onChange={(e) => setIncludeOwner(e.target.checked)} className="rounded" />
            Incluir Owner
          </label>

          <label className="min-h-11 flex items-center gap-2 text-sm text-sereno-dark/70 cursor-pointer">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => {
                const next = e.target.checked;
                setActiveOnly(next);
                if (next) setPropertyIds((ids) => ids.filter((id) => filtersData?.properties.find((p) => p.id === id)?.active));
              }}
              className="rounded"
            />
            Solo propiedades activas (PMS)
          </label>

          <label
            className="min-h-11 flex items-center gap-2 text-sm text-sereno-dark/70 cursor-pointer"
            title="Malia Bernal (3) y CasaML (2). Al desactivarlas salen de todos los cálculos."
          >
            <input
              type="checkbox"
              checked={includeFamily}
              onChange={(e) => {
                const next = e.target.checked;
                setIncludeFamily(next);
                // Al desactivarlas, quítalas también de la selección de propiedades
                const nextIds = next
                  ? propertyIds
                  : propertyIds.filter((id) => !filtersData?.properties.find((p) => p.id === id)?.family);
                setPropertyIds(nextIds);
                runCompare({ includeFamily: next, propertyIds: nextIds });
              }}
              className="rounded"
            />
            Incluir propiedades familiares
          </label>

          <FiltroAlicante
            valor={alicante}
            onChange={(next) => {
              setAlicante(next);
              const nextIds = propertyIds.filter((id) => {
                const p = filtersData?.properties.find((x) => x.id === id);
                if (!p) return true;
                return next === 'sin' ? !p.alicante : next === 'solo' ? p.alicante : true;
              });
              setPropertyIds(nextIds);
              runCompare({ alicante: next, propertyIds: nextIds });
            }}
          />

          <div className="min-h-11 flex items-center gap-2">
            <span className="text-sm text-sereno-dark/60">Imputar por:</span>
            <div className="inline-flex rounded-lg border border-sereno-dark/15 overflow-hidden">
              <button
                onClick={() => setProrate(true)}
                className={classNames('min-h-11 px-3 text-xs font-medium transition-colors', prorate ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20')}
              >
                Noche
              </button>
              <button
                onClick={() => setProrate(false)}
                className={classNames('min-h-11 px-3 text-xs font-medium transition-colors', !prorate ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20')}
              >
                Entrada
              </button>
            </div>
            <InfoTooltip text="Noche: el importe de cada reserva se reparte proporcionalmente entre las noches que caen dentro del periodo (devengo, igual que Hostify). Entrada: el importe completo se asigna al mes de check-in." />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <button onClick={() => runCompare()} disabled={loading} className="btn-primary min-h-11 flex items-center gap-2">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <GitCompareArrows size={16} />}
            Comparar
          </button>

          {activeFilterCount > 0 && (
            <button
              onClick={() => {
                setPropertyIds([]);
                setZones([]);
                setChannels([]);
                setIncludeOwner(false);
                setActiveOnly(true);
                setIncludeFamily(true);
                setAlicante('sin');
                setProrate(true);
              }}
              className="min-h-11 flex items-center gap-1.5 text-sm text-sereno-dark/50 hover:text-sereno-dark"
            >
              <X size={14} /> Limpiar filtros ({activeFilterCount})
            </button>
          )}
        </div>
      </PanelFiltros>

      {loading && !data ? (
        <div className="flex justify-center py-16"><Loader2 size={32} className="animate-spin text-sereno-dark/40" /></div>
      ) : !data ? (
        <div className="card text-center py-12 text-sereno-dark/50">No hay datos para comparar.</div>
      ) : (
        <>
          {/* Headline facturación comparison */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <HeadlineCard label={data.current.label} value={fmtMoney(data.current.facturacion)} sub="Periodo actual (A)" highlight />
            <HeadlineCard label={data.previous.label} value={fmtMoney(data.previous.facturacion)} sub="Periodo comparación (B)" />
            <DeltaCard delta={data.deltas.facturacion} />
          </div>

          {/* KPI comparison table */}
          <div className="card mb-6">
            <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-4">Comparativa de KPIs</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-sereno-dark/50 border-b border-sereno-dark/10">
                    <th className="py-2 font-medium">KPI</th>
                    <th className="py-2 font-medium text-right">{data.current.label} (A)</th>
                    <th className="py-2 font-medium text-right">{data.previous.label} (B)</th>
                    <th className="py-2 font-medium text-right">Variación</th>
                  </tr>
                </thead>
                <tbody>
                  {KPI_ROWS.map((row) => {
                    const cur = (data.current as any)[row.key] as number;
                    const prev = (data.previous as any)[row.key] as number;
                    const d = data.deltas[row.key];
                    const info = KPI_INFO[row.key];
                    return (
                      <tr key={row.key} className="border-b border-sereno-dark/5 last:border-0">
                        <td className="py-2.5">
                          <span className="inline-flex items-center gap-1.5">
                            {info?.label || row.key}
                            {info && <InfoTooltip text={info.info} />}
                          </span>
                        </td>
                        <td className="py-2.5 text-right font-semibold">{row.format(cur)}</td>
                        <td className="py-2.5 text-right text-sereno-dark/60">{row.format(prev)}</td>
                        <td className="py-2.5 text-right">
                          <DeltaBadge delta={d} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Channel comparison chart */}
          <div className="card">
            <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-4">Facturación por canal — A vs B</h2>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={channelChart} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: chart.axis }} />
                <YAxis tick={{ fontSize: 12, fill: chart.axis }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: any) => fmtMoney(Number(v))} cursor={{ fill: chart.cursor }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="actual" name={data.current.label} fill={chart.barA} radius={[4, 4, 0, 0]} />
                <Bar dataKey="anterior" name={data.previous.label} fill={chart.barB} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

function PeriodPicker({
  title, from, to, onFrom, onTo, accent,
}: { title: string; from: string; to: string; onFrom: (v: string) => void; onTo: (v: string) => void; accent: string }) {
  return (
    <div className="rounded-lg border border-sereno-dark/10 p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className={classNames('w-3 h-3 rounded-full', accent)} />
        <span className="text-xs font-semibold text-sereno-dark/70">{title}</span>
      </div>
      <div className="flex items-end gap-3">
        <div>
          <label className="block text-xs text-sereno-dark/50 mb-1">Desde</label>
          <input type="date" value={from} onChange={(e) => onFrom(e.target.value)} className="input min-h-11" />
        </div>
        <div>
          <label className="block text-xs text-sereno-dark/50 mb-1">Hasta</label>
          <input type="date" value={to} onChange={(e) => onTo(e.target.value)} className="input min-h-11" />
        </div>
      </div>
    </div>
  );
}

function HeadlineCard({ label, value, sub, highlight }: { label: string; value: string; sub: string; highlight?: boolean }) {
  return (
    <div className={classNames('card', highlight && 'ring-2 ring-sereno-dark/10')}>
      <p className="text-xs text-sereno-dark/40 mb-1">{sub}</p>
      <p className="text-sm font-medium text-sereno-dark/70 mb-1">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function DeltaCard({ delta }: { delta: { abs: number; pct: number | null } }) {
  const positive = delta.abs >= 0;
  const Icon = delta.abs === 0 ? Minus : positive ? TrendingUp : TrendingDown;
  const color = delta.abs === 0 ? 'text-sereno-dark/50' : positive ? 'text-emerald-600' : 'text-rose-600';
  return (
    <div className="card flex flex-col justify-center">
      <p className="text-xs text-sereno-dark/40 mb-1">Variación facturación</p>
      <div className={classNames('flex items-center gap-2', color)}>
        <Icon size={24} />
        <span className="text-2xl font-bold">
          {delta.pct === null ? '—' : `${positive ? '+' : ''}${Math.round(delta.pct!)}%`}
        </span>
      </div>
      <p className={classNames('text-sm font-medium mt-1', color)}>
        {positive ? '+' : ''}{fmtMoney(delta.abs)}
      </p>
    </div>
  );
}

function DeltaBadge({ delta }: { delta: { abs: number; pct: number | null } }) {
  if (!delta) return <span className="text-sereno-dark/30">—</span>;
  const positive = delta.abs >= 0;
  const Icon = delta.abs === 0 ? Minus : positive ? TrendingUp : TrendingDown;
  const color = delta.abs === 0 ? 'text-sereno-dark/40' : positive ? 'text-emerald-600' : 'text-rose-600';
  return (
    <span className={classNames('inline-flex items-center gap-1 font-semibold', color)}>
      <Icon size={14} />
      {delta.pct === null ? 'n/a' : `${positive ? '+' : ''}${Math.round(delta.pct!)}%`}
    </span>
  );
}
